import { signal } from "@preact/signals";
import type { LatLng } from "../../shared/types.js";

/**
 * The device's position, for distance to the green.
 *
 * Module-level like wakeLock.ts, for the same reason: no component owns the lifecycle,
 * and a screen that wants it just says start and stop.
 *
 * Two things this deliberately does not do:
 *
 *  - It never starts itself. Prompting for location on app load is the fastest way to
 *    get it denied, and a denial is sticky — the browser won't ask twice. The prompt is
 *    raised at the moment someone taps for distances, where it's obvious what they're
 *    granting it for.
 *  - It never blocks anything. Every failure path leaves `position` null, which the UI
 *    renders as nothing at all. Scoring does not read this file.
 */
export interface Fix {
  coords: LatLng;
  /** Metres, as the browser reports it — converted where it's displayed. */
  accuracy: number;
}

export const position = signal<Fix | null>(null);

/**
 * Set when there is no position to be had — the browser refused, or it tried and came
 * back with nothing. Either way the UI stops promising a number, rather than sitting on
 * "finding you" until the round ends.
 *
 * One signal for both because the difference doesn't change what anyone can do about it,
 * and a screen that stays quiet is the required behaviour in both cases.
 */
export const unavailable = signal(false);


const OPTIONS: PositionOptions = {
  // The whole feature is yardages; a coarse network fix would be worse than nothing.
  enableHighAccuracy: true,
  // A few seconds old is fine walking down a fairway, and reusing a recent fix is much
  // cheaper than forcing a fresh one.
  maximumAge: 5000,
  timeout: 20000,
};

let watchId: number | null = null;
/** Whether the app currently wants a position at all, as opposed to the watch being
 * suspended because the screen is in someone's pocket. */
let wanted = false;

function toFix(p: GeolocationPosition): Fix {
  return {
    coords: { lat: p.coords.latitude, lng: p.coords.longitude },
    accuracy: p.coords.accuracy,
  };
}

function begin(): void {
  if (watchId !== null) return;

  if (!navigator.geolocation) return;
  watchId = navigator.geolocation.watchPosition(
    (p) => {
      unavailable.value = false;
      position.value = toFix(p);
    },
    (err) => {
      unavailable.value = true;
      // A timeout or a lost fix is ordinary — under trees, inside a clubhouse — and
      // watchPosition keeps trying after one, so the watch is left running and a fix
      // that turns up later clears this again. A refusal is different: nothing will
      // change until the browser's own permission does, so stop draining the battery
      // asking a question that's already been answered.
      if (err.code === err.PERMISSION_DENIED) stopWatching();
    },
    OPTIONS,
  );
}

function end(): void {
  if (watchId !== null) navigator.geolocation.clearWatch(watchId);
  watchId = null;
}

export function startWatching(): void {
  wanted = true;
  begin();
}

export function stopWatching(): void {
  wanted = false;
  end();
  // Dropped rather than kept: a five-hour round means the next screen that reads this
  // could otherwise open showing a distance measured from a hole you finished an hour
  // ago, and a stale yardage is worse than none.
  position.value = null;
}

// A continuous high-accuracy watch is the expensive part of this feature over a
// five-hour round, and none of it is any use while the phone is in a pocket.
document.addEventListener("visibilitychange", () => {
  if (!wanted) return;
  if (document.visibilityState === "hidden") end();
  else begin();
});

/**
 * One position, once — for setting a green point by standing on it in the course-map
 * builder.
 *
 * Separate from the watch above because it's a different job: the watch follows a
 * player around a course, this answers "where am I standing right now" and then stops.
 */
export function capturePosition(): Promise<Fix> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This device can't report its location."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve(toFix(p)),
      (err) =>
        reject(
          new Error(
            err.code === err.PERMISSION_DENIED
              ? "Location is off for this app, so the green's position can't be set from here."
              : "Couldn't get a location fix — try again with a clear view of the sky.",
          ),
        ),
      { ...OPTIONS, maximumAge: 0 },
    );
  });
}
